import React, { useEffect, useState } from 'react';

import { IconClose, IconPencil } from '../../../assets/icons/icons';

import { Modal } from '../../ui/ModalProject';
import { useGetProductAnalogsQuery } from '../../../entities/project/api/projectApi';
import { DIMENSIONS_ORIGIN, PACKAGING_CLASS, SHIPPING_FORM } from '../../../entities/project/model/mappers';
import type { CarrierPrice, Position } from '../../../types/project';
import type { ProductAnalog } from '../../../types/results';
import { formatMoney, formatNumber } from '../../../utils/format';
import styles from './DimensionsModal.module.scss';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { AnalogDimensionsModal } from '../AnalogDimensionsModal/AnalogDimensionsModal';

interface DimensionsModalProps {
    projectId: string;
    onEdit?: () => void;
    position: Position | null;
    onClose: () => void;
}

const DASH = '—';

const ANALOG_POLL_MS = 7000;

const OUTLIER_HINT: Record<'high' | 'low', string> = {
    high: 'Завышенная цена — исключена из расчёта стоимости доставки',
    low: 'Заниженная цена — исключена из расчёта стоимости доставки',
};

const gr = (grams: number | null | undefined) =>
    grams && grams > 0 ? `${formatNumber(grams)} г` : DASH;

const money = (value: number | null | undefined) =>
    value && value > 0 ? formatMoney(value) : DASH;

const dims3 = (size: { length: number; width: number; height: number } | null) =>
    size ? `${formatNumber(size.length)} × ${formatNumber(size.width)} × ${formatNumber(size.height)} мм` : DASH;

const carrierPrice = (carrier: CarrierPrice) => {
    if (!carrier.min) return DASH;
    if (carrier.max && carrier.max !== carrier.min) {
        return `${formatMoney(carrier.min)} — ${formatMoney(carrier.max)}`;
    }
    return formatMoney(carrier.min);
};

const analogLabel = (item: ProductAnalog) =>
    [item.brand, item.model].filter(Boolean).join(' ') || item.host || item.title;

export function DimensionsModal({ projectId, position, onEdit, onClose }: DimensionsModalProps) {
    const [analogsInterval, setAnalogsInterval] = useState(ANALOG_POLL_MS);
    const [editingAnalog, setEditingAnalog] = useState<{ item: ProductAnalog; index: number } | null>(null);

    const {
        data: analogs,
        isError: analogsFailed,
        refetch: refetchAnalogs,
    } = useGetProductAnalogsQuery(position?.id ?? '', {
        skip: !position?.id || !position.analogs,
        pollingInterval: analogsInterval,
    });

    const analogsStage = !analogs ? 'idle' : analogs.isSearching ? 'search' : analogs.logistics;

    useEffect(() => {
        const busy = analogsStage === 'idle' || analogsStage === 'search' || analogsStage === 'processing';
        setAnalogsInterval(analogsFailed || !busy ? 0 : ANALOG_POLL_MS);
    }, [analogsStage, analogsFailed]);

    if (!position) return null;

    const own = position.dimensions;
    const hasOwn = Boolean(own && (own.length || own.width || own.height || own.weight));

    const usable = (position.offers ?? []).filter(
        (offer) => offer.dimensions && !offer.dimensions.failed && offer.dimensions.length,
    );

    const fallback = (
        usable.find((offer) => offer.dimensions?.origin === 'user_data') ?? usable[0]
    )?.dimensions;

    const size = hasOwn ? own : fallback;
    const origin = size?.origin ? DIMENSIONS_ORIGIN[size.origin] : null;
    const offers = position.offers ?? [];

    const carriers = position.carriers ?? [];

    const finalSize =
        size && (size.length || size.width || size.height)
            ? `${formatNumber(size.length ?? 0)} × ${formatNumber(size.width ?? 0)} × ${formatNumber(size.height ?? 0)} мм`
            : DASH;

    const finalWeight = gr(size?.weight);

    const transport = position.transport;
    const hasTransport = Boolean(transport && !transport.isEmpty);

    const transportSize = hasTransport
        ? `${formatNumber(transport?.length ?? 0)} × ${formatNumber(transport?.width ?? 0)} × ${formatNumber(transport?.height ?? 0)} мм`
        : finalSize;

    const transportWeight = hasTransport ? gr(transport?.weight) : finalWeight;

    const packaging = transport?.packagingClass
        ? PACKAGING_CLASS[transport.packagingClass] ?? transport.packagingClass
        : '';

    const unconfirmed = size?.confirmed === false && size?.origin !== 'not_calculated';
    const unconfirmedLabel = size?.origin === 'user_data' ? 'на проверке' : 'не подтверждено';

    const analogItems = analogs?.items ?? [];
    const analogsSearching = analogsStage === 'idle' || analogsStage === 'search';
    const analogsLogisticsPending = analogsStage === 'processing';
    const analogsNoLogistics = analogsStage === 'skipped';
    const showAnalogs = Boolean(position.analogs) && !analogsFailed && analogs?.requested !== false;

    const handleAnalogSaved = () => {
        setAnalogsInterval(ANALOG_POLL_MS);
        refetchAnalogs();
    };

    const columns = [
        {
            key: 'main',
            title: 'Основной товар',
            priceOf: carrierPrice,
            delivery: money(position.deliveryWithoutPackaging),
            packaging: money(position.packagingCost),
            total: money(position.deliveryCost),
            outlierOf: (carrier: CarrierPrice) => (carrier.min ? carrier.outlier : null),
        },
        ...(showAnalogs && !analogsNoLogistics
            ? analogItems.map((item, index) => {
                const busy = analogsLogisticsPending && !item.delivery;
                const entryOf = (carrier: CarrierPrice) =>
                    item.deliveryPrices.find((price) => price.id === carrier.id);

                return {
                    key: `analog-${index}`,
                    title: `Аналог №${index + 1}`,
                    priceOf: (carrier: CarrierPrice) =>
                        busy ? 'считаем…' : money(entryOf(carrier)?.price),
                    delivery: busy ? 'считаем…' : money(item.delivery?.withoutPackaging),
                    packaging: busy ? 'считаем…' : money(item.delivery?.packaging),
                    total: busy ? 'считаем…' : money(item.delivery?.avg),
                    outlierOf: (carrier: CarrierPrice) => {
                        if (busy) return null;
                        const entry = entryOf(carrier);
                        return entry?.price ? entry.outlier ?? null : null;
                    },
                };
            })
            : []),
    ];

    const groups = Array.from(
        offers.reduce((acc, offer) => {
            const own = offer.dimensions;
            const key = own?.failed
                ? 'failed'
                : `${own?.length ?? 0}x${own?.width ?? 0}x${own?.height ?? 0}x${own?.weight ?? 0}`;

            const seller = { id: offer.id, host: offer.host || offer.name, url: offer.url };
            const found = acc.get(key);

            if (found) {
                found.sellers.push(seller);
                return acc;
            }

            const hasSize = own && own.length && own.width && own.height;

            acc.set(key, {
                key,
                failed: Boolean(own?.failed),
                size: own?.failed
                    ? 'не определены'
                    : hasSize
                        ? `${formatNumber(own.length as number)} × ${formatNumber(own.width as number)} × ${formatNumber(own.height as number)} мм`
                        : DASH,
                weight: own?.failed ? DASH : `${gr(own?.weight)}${own?.isGross ? ' брутто' : ''}`,
                origin: offer.deliveryPending
                    ? 'считаем…'
                    : own?.origin
                        ? DIMENSIONS_ORIGIN[own.origin] ?? own.origin
                        : DASH,
                sellers: [seller],
            });

            return acc;
        }, new Map<string, {
            key: string;
            failed: boolean;
            size: string;
            weight: string;
            origin: string;
            sellers: { id: string; host: string; url: string }[];
        }>()).values(),
    );

    return (
        <Modal open onClose={onClose} ariaLabel="Информация по габаритам" className={styles.modal}>
            <header className={styles.head}>
                <div>
                    <h2>Информация по габаритам</h2>
                    <p>{position.name}</p>
                </div>

                <button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть">
                    <IconClose />
                </button>
            </header>

            <div className={styles.body}>
                <section className={styles.block}>
                    <h3 className={styles.sectionHead}>
                        Стоимость доставки
                        <em>(расчёт производится по транспортным габаритам)</em>
                    </h3>

                    {position.deliveryProcessing && (
                        <p className={styles.note}>Идёт пересчёт, значения могут измениться.</p>
                    )}

                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Перевозчик</th>
                                {columns.map((column) => (
                                    <th key={column.key}>{column.title}</th>
                                ))}
                                <th className={styles.spacer} />
                            </tr>
                        </thead>
                        <tbody>
                            {carriers.map((carrier) => (
                                <tr key={carrier.id}>
                                    <td>{carrier.name}</td>
                                    {columns.map((column) => {
                                        const outlier = column.outlierOf(carrier);

                                        return (
                                            <td key={column.key}>
                                                <span className={styles.priceCell}>
                                                    {column.priceOf(carrier)}
                                                    {outlier && (
                                                        <Tooltip text={OUTLIER_HINT[outlier]}>
                                                            <span className={styles.outlier} aria-label={OUTLIER_HINT[outlier]}>
                                                                <svg viewBox="0 0 20 20" aria-hidden="true">
                                                                    <path d="M10 3.5v8.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                                                    <circle cx="10" cy="16" r="1.6" fill="currentColor" />
                                                                </svg>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </span>
                                            </td>
                                        );
                                    })}
                                    <td className={styles.spacer} />
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className={styles.total}>
                                <td>Средняя стоимость доставки</td>
                                {columns.map((column) => <td key={column.key}>{column.delivery}</td>)}
                                <td className={styles.spacer} />
                            </tr>
                            <tr className={styles.total}>
                                <td>Стоимость упаковки</td>
                                {columns.map((column) => <td key={column.key}>{column.packaging}</td>)}
                                <td className={styles.spacer} />
                            </tr>
                            <tr className={styles.total}>
                                <td>Общая стоимость доставки</td>
                                {columns.map((column) => <td key={column.key}>{column.total}</td>)}
                                <td className={styles.spacer} />
                            </tr>
                        </tfoot>
                    </table>

                    <p className={styles.note}>
                        Прочерк означает, что перевозчик не рассчитал доставку по этой позиции.
                    </p>
                </section>

                <section className={styles.block}>
                    <h3>Габариты основного товара</h3>

                    {groups.length === 0 ? (
                        <p className={styles.note}>Предложений с габаритами пока нет.</p>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Основной товар</th>
                                    <th>Д × Ш × В</th>
                                    <th>Вес</th>
                                    <th>Откуда</th>
                                    <th>Продавцы</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((group, index) => (
                                    <tr key={group.key} className={group.failed ? styles.failed : undefined}>
                                        {index === 0 && (
                                            <td rowSpan={groups.length}>
                                                <Tooltip text={position.name}>
                                                    <span className={styles.cellName}>{position.name}</span>
                                                </Tooltip>
                                            </td>
                                        )}
                                        <td>{group.size}</td>
                                        <td>{group.weight}</td>
                                        <td>{group.origin}</td>
                                        <td className={styles.sellers}>
                                            {group.sellers.map((seller) => (
                                                <a key={seller.id} href={seller.url} target="_blank" rel="noreferrer">
                                                    {seller.host}
                                                </a>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>

                <div className={styles.finals}>
                    <dl className={styles.final}>
                        <dt>
                            Габариты товара (эталон)
                            {unconfirmed && (
                                <span className={styles.flag}>{unconfirmedLabel}</span>
                            )}
                        </dt>
                        <dd>
                            {finalSize}
                            {finalWeight !== DASH && `, ${finalWeight}`}
                            {origin && <em className={styles.origin}>{origin}</em>}
                            {unconfirmed && size?.reasoning && (
                                <em className={styles.reason}>{size.reasoning}</em>
                            )}
                        </dd>
                    </dl>

                    <dl className={styles.final}>
                        <dt>
                            Транспортные габариты
                            {transport?.isManual && <span className={styles.flag}>вручную</span>}
                            {transport?.oversize && (
                                <span className={styles.flagWarn}>негабарит</span>
                            )}
                        </dt>
                        <dd>
                            {transportSize}
                            {transportWeight !== DASH && `, ${transportWeight}`}
                            {packaging && <em className={styles.origin}>{packaging}</em>}
                            {!hasTransport && finalSize !== DASH && (
                                <em className={styles.origin}>
                                    Совпадают с габаритами товара
                                </em>
                            )}
                        </dd>
                    </dl>

                    {onEdit && (
                        <Tooltip text="Изменить габариты" align="right">
                            <button
                                type="button"
                                className={styles.editBtn}
                                aria-label="Изменить габариты"
                                onClick={onEdit}
                            >
                                <IconPencil />
                            </button>
                        </Tooltip>
                    )}
                </div>

                {showAnalogs && (
                    <section className={styles.block}>
                        <h3>Габариты аналогов</h3>

                        {analogsSearching ? (
                            <p className={styles.note}>Подбираем аналоги…</p>
                        ) : analogItems.length === 0 ? (
                            <p className={styles.note}>Подходящих аналогов не нашлось.</p>
                        ) : analogsNoLogistics ? (
                            <p className={styles.note}>
                                Расчёт доставки для этой позиции выключен — габариты аналогов не считались.
                            </p>
                        ) : (
                            <>
                                {analogsLogisticsPending && (
                                    <p className={styles.note}>Считаем габариты и доставку, значения появятся.</p>
                                )}

                                {analogItems.map((item, index) => {
                                    const hasDims = Boolean(item.dimensionsOrigin);
                                    const pending = analogsLogisticsPending && !hasDims;
                                    const undefinedDims = !pending && !hasDims;

                                    const manual = item.dimensionsOrigin === 'user_data';
                                    const dimsOrigin = item.dimensionsOrigin
                                        ? DIMENSIONS_ORIGIN[item.dimensionsOrigin] ?? item.dimensionsOrigin
                                        : null;
                                    const analogPackaging = hasDims && item.packagingClass
                                        ? PACKAGING_CLASS[item.packagingClass] ?? item.packagingClass
                                        : '';
                                    const shipping = hasDims ? item.shipping : null;

                                    const analogSize = pending ? 'считаем…' : hasDims ? dims3(item.dimensions) : DASH;
                                    const analogWeight = pending || !hasDims ? '' : gr(item.dimensions?.weight);
                                    const sameAsMain = hasDims && !item.transport && Boolean(item.dimensions);
                                    const analogTransportSize = pending
                                        ? 'считаем…'
                                        : !hasDims
                                            ? DASH
                                            : item.transport
                                                ? dims3(item.transport)
                                                : analogSize;
                                    const analogTransportWeight = pending || !hasDims
                                        ? ''
                                        : item.transport
                                            ? gr(item.transport.weight)
                                            : analogWeight;

                                    return (
                                        <div key={item.url || index} className={styles.analog}>
                                            <table className={styles.table}>
                                                <thead>
                                                    <tr>
                                                        {/* Номер совпадает с колонкой в таблице доставки */}
                                                        <th>Аналог №{index + 1}</th>
                                                        <th>Д × Ш × В</th>
                                                        <th>Вес</th>
                                                        <th>Откуда</th>
                                                        <th>Продавцы</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className={undefinedDims ? styles.failed : undefined}>
                                                        <td>
                                                            <Tooltip text={item.title}>
                                                                <span className={styles.cellName}>{analogLabel(item)}</span>
                                                            </Tooltip>
                                                        </td>
                                                        <td>{analogSize}</td>
                                                        <td>{pending ? 'считаем…' : analogWeight || DASH}</td>
                                                        <td>{pending ? 'считаем…' : dimsOrigin ?? DASH}</td>
                                                        <td className={styles.sellers}>
                                                            <a href={item.url} target="_blank" rel="noreferrer" title={item.title}>
                                                                {item.host || analogLabel(item)}
                                                            </a>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            <div className={`${styles.finals} ${styles.analogFinals}`}>
                                                <dl className={styles.final}>
                                                    <dt>
                                                        Габариты товара (эталон)
                                                        {hasDims && item.dimensions && !item.dimensionsConfirmed && (
                                                            <span className={styles.flag}>
                                                                {manual ? 'на проверке' : 'не подтверждено'}
                                                            </span>
                                                        )}
                                                    </dt>
                                                    <dd>
                                                        {analogSize}
                                                        {analogWeight && analogWeight !== DASH && `, ${analogWeight}`}
                                                        {dimsOrigin && <em className={styles.origin}>{dimsOrigin}</em>}
                                                        {hasDims && !item.dimensionsConfirmed && item.dimensionsReasoning && (
                                                            <em className={styles.reason}>{item.dimensionsReasoning}</em>
                                                        )}
                                                    </dd>
                                                </dl>

                                                <dl className={styles.final}>
                                                    <dt>
                                                        Транспортные габариты
                                                        {hasDims && item.oversize && <span className={styles.flagWarn}>негабарит</span>}
                                                    </dt>
                                                    <dd>
                                                        {analogTransportSize}
                                                        {analogTransportWeight && analogTransportWeight !== DASH && `, ${analogTransportWeight}`}
                                                        {analogPackaging && <em className={styles.origin}>{analogPackaging}</em>}
                                                        {shipping && shipping.places && (
                                                            <em className={styles.origin}>
                                                                {shipping.places} {SHIPPING_FORM[shipping.form] ?? 'мест'}
                                                                {shipping.unitLengthM ? ` по ${shipping.unitLengthM} м` : ''}
                                                                {shipping.estimated ? ' (оценка)' : ''}
                                                            </em>
                                                        )}
                                                        {(sameAsMain || (hasDims && item.transportMatchesMain)) && analogSize !== DASH && (
                                                            <em className={styles.origin}>Совпадают с габаритами товара</em>
                                                        )}
                                                    </dd>
                                                </dl>

                                                {!pending && (
                                                    <Tooltip text="Изменить габариты" align="right">
                                                        <button
                                                            type="button"
                                                            className={styles.editBtn}
                                                            aria-label={`Изменить габариты аналога ${index + 1}`}
                                                            onClick={() => setEditingAnalog({ item, index })}
                                                        >
                                                            <IconPencil />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                <p className={styles.note}>
                                    Аналог — другой товар: у него своя упаковка и своя стоимость доставки,
                                    в итог по позиции они не входят.
                                </p>
                            </>
                        )}
                    </section>
                )}
            </div>

            <AnalogDimensionsModal
                analog={editingAnalog}
                projectId={projectId}
                productId={position.id}
                onClose={() => setEditingAnalog(null)}
                onSaved={handleAnalogSaved}
            />
        </Modal>
    );
}
